import { html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { BaseElement } from '../base/base-element.js';

export type ConductorMode = 'default' | 'practice' | 'test';

const MODES: { value: ConductorMode; label: string }[] = [
    { value: 'default', label: 'Default' },
    { value: 'practice', label: 'Practice' },
    { value: 'test', label: 'Test' },
];

/**
 * Three-way segmented control for conductor modes (Default / Practice / Test).
 *
 * @fires mode-change - detail: { mode: ConductorMode }
 */
@customElement('mode-selector')
export class ModeSelector extends BaseElement {
    static styles = [
        BaseElement.styles,
        css`
            :host {
                display: inline-flex;
                align-items: center;
                height: 41px;
                background-color: #E5E5EA;
                border-radius: 41px;
                padding: 2px;
                user-select: none;
            }

            :host([disabled]) {
                opacity: 0.5;
                pointer-events: none;
            }

            button {
                min-width: 45px;
                height: 37px;
                padding: 0 10px;
                border-radius: 37px;
                font-size: 12px;
                font-weight: 600;
                color: #3C3C43;
                background: transparent;
                transition: background-color 0.2s ease, color 0.2s ease;
            }

            button[aria-checked="true"] {
                background-color: white;
                color: #000;
                box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
            }

            button:focus-visible {
                outline: 2px solid #4CD964;
                outline-offset: 1px;
            }
        `,
    ];

    @property({ type: String, reflect: true })
    mode: ConductorMode = 'default';

    @property({ type: Boolean, reflect: true })
    disabled = false;

    private _select(mode: ConductorMode): void {
        if (this.disabled || mode === this.mode) return;
        this.mode = mode;
        this.dispatchEvent(
            new CustomEvent<{ mode: ConductorMode }>('mode-change', {
                bubbles: true,
                composed: true,
                detail: { mode },
            })
        );
    }

    render() {
        return html`
            <div role="radiogroup" aria-label="Conductor mode">
                ${MODES.map(
                    (m) => html`
                        <button
                            type="button"
                            role="radio"
                            aria-checked=${m.value === this.mode}
                            tabindex=${m.value === this.mode ? 0 : -1}
                            @click=${() => this._select(m.value)}
                        >
                            ${m.label}
                        </button>
                    `
                )}
            </div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'mode-selector': ModeSelector;
    }
}
